import CloseOutlined from '@mui/icons-material/CloseOutlined'
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined'

import {
  SmallText,
  SmallTextBold,
  SmallTextDense,
} from '../../data-display/typography/Typography'

import React, { type ButtonHTMLAttributes } from 'react'
import {
  Badge,
  StyledButtonBase,
  StyledClearButton,
  StyledClearableChip,
  StyledOpenButton,
} from './FilterItem.styled'

export type FilterItemSize = 'small' | 'medium'

/** `warning` draws the count as a yellow tag: a count that asks the user to act. */
export type FilterItemSelectedColor = 'brand' | 'warning'

type ClearProps =
  | { onClear?: never; clearAriaLabel?: never }
  | {
      /**
       * Clears every value the chip counts. Renders a clear button next to
       * the label while `selected` is greater than 0.
       */
      onClear: () => void
      /** Accessible name of the clear button, translated by the caller. */
      clearAriaLabel: string
    }

export type FilterItemProps = {
  label: string
  /** Number of selected values, shown as a badge when greater than 0. */
  selected?: number
  /** Colour of the `selected` badge. Defaults to the brand badge. */
  selectedColor?: FilterItemSelectedColor
  /**
   * Toggle state of a single-value filter (a switch rendered as a chip):
   * filled background and brand border, no badge.
   */
  active?: boolean
  /**
   * Bold label, active or not. Opt-in: the other chips keep their regular
   * label.
   */
  boldLabel?: boolean
  onClick: () => void
  icon?: React.JSX.Element
  dataTestId?: string
  withIcon?: boolean
  iconPosition?: 'start' | 'end'
  disabled?: boolean
  size?: FilterItemSize
  'aria-haspopup'?: ButtonHTMLAttributes<HTMLButtonElement>['aria-haspopup']
  'aria-expanded'?: boolean
  'aria-controls'?: string
} & ClearProps

export const FilterItem = ({
  label,
  selected,
  selectedColor = 'brand',
  active,
  boldLabel = false,
  onClick,
  icon = <ExpandMoreOutlined />,
  dataTestId,
  withIcon = true,
  iconPosition = 'end',
  disabled = false,
  size = 'medium',
  'aria-haspopup': ariaHaspopup = 'dialog',
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
  onClear,
  clearAriaLabel,
}: FilterItemProps) => {
  const Label = size === 'small' ? SmallTextDense : SmallText
  const hasSelectedValues = selected !== undefined && selected > 0
  // A warning count asks to act, it does not count selected values.
  const ariaLabelSuffix = selectedColor === 'warning' ? '' : ' selected'

  const buttonProps = {
    disableRipple: true,
    disabled,
    onClick,
    'data-testid': dataTestId,
    'aria-haspopup': ariaHaspopup,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'aria-pressed': active,
    'aria-label': hasSelectedValues
      ? `${label}, ${selected}${ariaLabelSuffix}`
      : undefined,
  }

  const content = (
    <>
      {withIcon && iconPosition === 'start' && icon}
      <Label sx={boldLabel ? { fontWeight: 'fontWeightBold' } : undefined}>
        {label}
      </Label>
      {hasSelectedValues ? (
        <Badge $color={selectedColor}>
          <SmallTextBold>{selected}</SmallTextBold>
        </Badge>
      ) : null}
      {withIcon && iconPosition === 'end' && icon}
    </>
  )

  if (onClear) {
    return (
      <StyledClearableChip
        $size={size}
        active={active}
        className={disabled ? 'Mui-disabled' : undefined}
      >
        <StyledOpenButton {...buttonProps}>{content}</StyledOpenButton>
        {hasSelectedValues && (
          <StyledClearButton
            size="small"
            ariaLabel={clearAriaLabel}
            onClick={onClear}
            disabled={disabled}
            data-testid={dataTestId && `${dataTestId}__clear`}
          >
            <CloseOutlined />
          </StyledClearButton>
        )}
      </StyledClearableChip>
    )
  }

  return (
    <StyledButtonBase $size={size} active={active} {...buttonProps}>
      {content}
    </StyledButtonBase>
  )
}
